import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { useA11yDialog, A11yDialogProps } from 'react-a11y-dialog';
import styles from './Popup.module.scss';
import { TChildren } from '../../typings/common';
import CloseBtn from './CloseBtn/CloseBtn';
import { usePopupFocusTrap } from '../../hooks/popup/usePopupFocusTrap';

export interface Props {
  isOpen: boolean;
  onClose?: () => void;
  id: string;
  title?: string | React.ReactNode;
  children: TChildren;
  size?: 'base' | 'lg';
  role?: A11yDialogProps['role'];
  dialogClassName?: string;
}

const Popup: React.FC<Props> = ({
  isOpen,
  title,
  id,
  children,
  onClose,
  size = 'base',
  dialogClassName,
  role = 'dialog',
}) => {
  const [instance, attr] = useA11yDialog({
    id,
    title,
    role,
  });

  usePopupFocusTrap(isOpen);

  useEffect(() => {
    if (!instance) {
      return;
    }

    if (isOpen) {
      instance.show();
    } else {
      instance.hide();
    }
  }, [isOpen, instance]);

  return createPortal(
    <div
      {...attr.container}
      className={classNames(styles.container)}>
      <div
        {...attr.overlay}
        className={classNames(styles.overlay)}
        role='presentation'
        onClick={onClose}
      />
      <div
        {...attr.dialog}
        className={classNames(
          styles.dialog,
          styles[`size-${size}`],
          dialogClassName
        )}>
        {onClose && (
          <div className={styles.closeBtn}>
            <CloseBtn onClick={onClose} />
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};

export default Popup;
